/**
 * The funnel's state gate (FR-BOOK-03).
 *
 * Two lists, both verbatim from the captured DOM
 * (`apps/patient/parity/book/dom.html`, `insurance/dom.html`):
 *
 *  - `LICENSED_STATES` is the gate's own option list, in the live page's order
 *    (alphabetical, then the "not listed" escape hatch).
 *  - `OTHER_STATES` is the second select that appears inside the unavailable panel and
 *    is the list of everywhere HeyAllergy does NOT practise — which is why it is not
 *    simply "the other 42": District of Columbia is in it and the eight licensed states
 *    are not.
 *
 * The two "not available" paragraphs differ between `/book` and `/insurance` in the
 * ORDER of the states they name, and both are reproduced exactly (ADR-008).
 *
 * TODO(M5.7): the licensed list is per tenant and belongs to the branding endpoint
 * (FR-PLAT-03); these constants become its fallback.
 */

/** The sentinel value the live `<select>` uses for "My state is not listed". */
export const STATE_NOT_LISTED = 'other';

export const LICENSED_STATES: readonly string[] = [
  'California',
  'Florida',
  'Georgia',
  'Illinois',
  'Nevada',
  'New York',
  'Pennsylvania',
  'Texas',
];

/** `/book`'s wording of the unavailable message. */
export const STATE_UNAVAILABLE_BODY_BOOK =
  'HeyAllergy currently provides telemedicine services in California, Texas, Florida, Georgia, New York, Illinois, Nevada, and Pennsylvania. We hope to expand to your state soon.';

/** `/insurance`'s wording — same sentence, different order of states. Preserved as-is. */
export const STATE_UNAVAILABLE_BODY_INSURANCE =
  'HeyAllergy currently provides telemedicine services in California, Florida, Georgia, New York, Texas, Illinois, Nevada, and Pennsylvania. We hope to expand to your state soon.';

export const STATE_UNAVAILABLE_TITLE = 'Service Not Available in Your State';

/** The "Which state are you in?" list inside the unavailable panel, on `/book` only. */
export const OTHER_STATES: readonly string[] = [
  'Alabama',
  'Alaska',
  'Arizona',
  'Arkansas',
  'Colorado',
  'Connecticut',
  'Delaware',
  'District of Columbia',
  'Hawaii',
  'Idaho',
  'Indiana',
  'Iowa',
  'Kansas',
  'Kentucky',
  'Louisiana',
  'Maine',
  'Maryland',
  'Massachusetts',
  'Michigan',
  'Minnesota',
  'Mississippi',
  'Missouri',
  'Montana',
  'Nebraska',
  'New Hampshire',
  'New Jersey',
  'New Mexico',
  'North Carolina',
  'North Dakota',
  'Ohio',
  'Oklahoma',
  'Oregon',
  'Rhode Island',
  'South Carolina',
  'South Dakota',
  'Tennessee',
  'Utah',
  'Vermont',
  'Virginia',
  'Washington',
  'West Virginia',
  'Wisconsin',
  'Wyoming',
  'Other / Outside the US',
];

export const OTHER_STATE_THANKS =
  "Thank you — we've noted your interest. We use this to decide where to expand next.";

/* ------------------------------------------------------------------ *
 * Codes
 * ------------------------------------------------------------------ */

/**
 * Two-letter code to display name, for every place a patient could be (M5.7).
 *
 * The platform speaks codes and the funnel speaks names, and the two must not be
 * confused: `StateCodeSchema` (packages/contracts/src/scheduling.ts) is the only thing
 * `GET /public/tenant`, `?state=` on availability, `POST /public/reservations`,
 * `PATCH /public/drafts/current` and `state_unavailable` will accept, while every string
 * the live page shows - the option labels, the two "not available" paragraphs - is a
 * name. So the funnel keeps NAMES in its draft (which is what `ResumePrompt` and the
 * copy read) and converts at the API boundary, in `stateCode` / `stateName` and nowhere
 * else.
 *
 * District of Columbia is included because `OTHER_STATES` lists it: a visitor there must
 * be able to tell us where they are, which is the whole point of FR-BOOK-03's interest
 * event.
 */
export const US_STATE_NAME_BY_CODE: Readonly<Record<string, string>> = Object.freeze({
  AL: 'Alabama',
  AK: 'Alaska',
  AZ: 'Arizona',
  AR: 'Arkansas',
  CA: 'California',
  CO: 'Colorado',
  CT: 'Connecticut',
  DE: 'Delaware',
  DC: 'District of Columbia',
  FL: 'Florida',
  GA: 'Georgia',
  HI: 'Hawaii',
  ID: 'Idaho',
  IL: 'Illinois',
  IN: 'Indiana',
  IA: 'Iowa',
  KS: 'Kansas',
  KY: 'Kentucky',
  LA: 'Louisiana',
  ME: 'Maine',
  MD: 'Maryland',
  MA: 'Massachusetts',
  MI: 'Michigan',
  MN: 'Minnesota',
  MS: 'Mississippi',
  MO: 'Missouri',
  MT: 'Montana',
  NE: 'Nebraska',
  NV: 'Nevada',
  NH: 'New Hampshire',
  NJ: 'New Jersey',
  NM: 'New Mexico',
  NY: 'New York',
  NC: 'North Carolina',
  ND: 'North Dakota',
  OH: 'Ohio',
  OK: 'Oklahoma',
  OR: 'Oregon',
  PA: 'Pennsylvania',
  RI: 'Rhode Island',
  SC: 'South Carolina',
  SD: 'South Dakota',
  TN: 'Tennessee',
  TX: 'Texas',
  UT: 'Utah',
  VT: 'Vermont',
  VA: 'Virginia',
  WA: 'Washington',
  WV: 'West Virginia',
  WI: 'Wisconsin',
  WY: 'Wyoming',
});

const CODE_BY_NAME: Readonly<Record<string, string>> = Object.freeze(
  Object.fromEntries(
    Object.entries(US_STATE_NAME_BY_CODE).map(([code, name]) => [name.toLowerCase(), code]),
  ),
);

/**
 * The two-letter code the API wants, or `null` for the empty selection, the
 * "not listed" sentinel and anything unrecognised.
 *
 * `null` rather than a throw: a state the platform has no code for must degrade to "the
 * funnel cannot ask for availability", not to a blank page.
 */
export function stateCode(name: string): string | null {
  const trimmed = name.trim();
  if (trimmed === '' || trimmed === STATE_NOT_LISTED) return null;
  if (/^[A-Za-z]{2}$/.test(trimmed)) {
    const upper = trimmed.toUpperCase();
    return upper in US_STATE_NAME_BY_CODE ? upper : null;
  }
  return CODE_BY_NAME[trimmed.toLowerCase()] ?? null;
}

/**
 * The display name for a code from the tenant or from a saved draft.
 *
 * Falls back to the code itself so a clinic that licenses a territory this table does
 * not know still renders a usable option rather than an empty one.
 */
export function stateName(code: string): string {
  const upper = code.trim().toUpperCase();
  return US_STATE_NAME_BY_CODE[upper] ?? code.trim();
}
